import React, { useCallback, useEffect, useState } from 'react';
import { FileText, Download, FolderOpen, RefreshCw } from 'lucide-react';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { Skeleton } from '../ui/skeleton';
import { Badge } from '../ui/badge';
import { easerGet, errorMessage, documentUrl, fmtShortDate, getStoredUser } from '../../lib/easerApi';
import { getSeenDocs, markDocsSeen } from '../../lib/docsSeen';

const fmtSize = (n) => (n > 1024 * 1024 ? `${(n / 1024 / 1024).toFixed(1)} Mo` : `${Math.max(1, Math.round(n / 1024))} Ko`);

export function EaserDocuments({ onUnauthorized }) {
  const [items, setItems] = useState(null);
  const [error, setError] = useState('');
  const userId = getStoredUser()?.id;
  // Snapshot of what was already seen when the tab opened: new files keep their « Nouveau » badge while on the tab.
  const [seenAtOpen] = useState(() => getSeenDocs(userId));

  const load = useCallback(async () => {
    setError('');
    setItems(null);
    try {
      const res = await easerGet('/api/easer/documents');
      setItems(res.data.items);
      markDocsSeen(userId, res.data.items); // opening the tab clears the red dot
    } catch (err) {
      if (err?.response?.status === 401) return onUnauthorized();
      setError(errorMessage(err, 'Impossible de charger les documents'));
      setItems([]);
    }
  }, [onUnauthorized, userId]);

  useEffect(() => { load(); }, [load]);

  if (items === null) return <div className="space-y-3">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-16 rounded-xl" />)}</div>;

  return (
    <div className="space-y-4" data-testid="easer-documents">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">Bulletins, contrats et documents déposés par l'agence sur votre fiche Dolibarr.</p>
        <Button variant="ghost" size="icon" onClick={load} aria-label="Actualiser" data-testid="documents-refresh"><RefreshCw className="h-4 w-4" /></Button>
      </div>
      {error && (
        <Card className="p-5 text-center"><p className="text-sm text-destructive mb-3">{error}</p><Button variant="outline" size="sm" onClick={load}>Réessayer</Button></Card>
      )}
      {!error && items.length === 0 && (
        <Card className="p-8 text-center" data-testid="documents-empty">
          <FolderOpen className="h-10 w-10 mx-auto text-muted-foreground mb-3" />
          <p className="font-medium">Aucun document pour le moment</p>
          <p className="text-sm text-muted-foreground mt-1">Vos documents apparaîtront ici dès que l'agence les aura déposés.</p>
        </Card>
      )}
      {items.length > 0 && (
        <ul className="space-y-2" data-testid="documents-list">
          {items.map((d) => {
            const isNew = !seenAtOpen.has(d.relativename);
            return (
            <li key={d.relativename}>
              <a href={documentUrl(d.relativename)} target="_blank" rel="noopener noreferrer" className="block" data-testid="document-item">
                <Card className={`p-3.5 flex items-center gap-3 shadow-sm hover:border-[hsl(var(--primary))] transition-colors ${isNew ? 'border-[hsl(var(--accent))]/60 bg-[hsl(var(--accent))]/5' : 'border-border'}`}>
                  <div className="relative w-10 h-10 rounded-lg bg-[hsl(var(--brand-mint))] flex items-center justify-center flex-shrink-0">
                    <FileText className="h-5 w-5 text-[hsl(var(--primary))]" />
                    {isNew && <span className="absolute -top-1 -right-1 h-3 w-3 rounded-full bg-destructive ring-2 ring-background" aria-hidden="true" />}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="font-medium text-sm truncate flex items-center gap-2">{d.name}{isNew && <Badge className="bg-destructive text-white hover:bg-destructive text-[10px] px-1.5 py-0" data-testid="document-new-badge">Nouveau</Badge>}</p>
                    <p className="text-xs text-muted-foreground">{fmtShortDate(d.date)}{d.size ? ` · ${fmtSize(d.size)}` : ''}</p>
                  </div>
                  <Download className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                </Card>
              </a>
            </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
